import type { Escala, Pregunta } from './esquemas/cuestionarios';

/**
 * El recorrido plegado (CC-12): cómo se resume una respuesta en una línea.
 *
 * ── Por qué esto vive en `shared/` y no en cada lado ──
 * Lo dibujan **dos** sitios: la previa del constructor en la web, y la app que
 * responde el trabajador. Y tienen que decir **lo mismo**: quien arma el
 * cuestionario aprueba lo que ve en la previa, así que si la previa resume «De
 * acuerdo» y el teléfono dice «Punto 4», aprobó algo que nadie va a ver.
 *
 * Ya pasó dos veces en este proyecto —`acentoDeColor` y el emparejamiento de las
 * imágenes del set— y las dos veces el síntoma fue el mismo: **nada falla, sale
 * distinto**. No hay prueba que lo agarre, porque cada lado pasa la suya.
 *
 * ⚠️ **No está en `esquemas/` a propósito.** Acá no hay nada de Zod ni ningún
 * contrato de API: es lógica de presentación compartida. Meterla entre los
 * esquemas haría creer que valida algo.
 *
 * ── Y por qué la forma es propia, y no `ValorRespuesta` ──
 * El envío de verdad tiene otra forma —una fila por opción marcada, una por
 * afirmación de matriz— porque va a una base con restricciones. Resumir desde ahí
 * obligaría a reagrupar filas para escribir una línea de texto. Cada lado adapta
 * su estado a esta forma, que es la mínima para poder resumir.
 */
export type RespuestaResumible = {
  /** likert y barra. En barra es el valor del recorrido, no un punto de escala. */
  punto?: number;
  /** opcion_unica. */
  opcion?: number;
  /** opcion_multiple. */
  opciones?: number[];
  /** si_no. */
  bool?: boolean;
  texto?: string;
  numero?: string;
  /** Siempre `aaaa-mm-dd`. Quien la tenga de otra forma la convierte antes. */
  fecha?: string;
  /** matriz_likert: por id de afirmación, el punto elegido. */
  matriz?: Record<number, number | undefined>;
  /** archivo, nota_voz y video. `nombre` es para leer, nunca el del archivo original. */
  adjunto?: { nombre: string; segundos?: number };
};

/**
 * De la fecha **como la teclea la persona** (`dd/mm/aaaa`) a la que usan el
 * contrato, la base y este módulo (`aaaa-mm-dd`). `null` si no tiene esa forma.
 *
 * ── Por qué es una función y no dos líneas donde toque ──
 * Porque en la app ya estaba escrita una vez —al enviar— y el resumen de la fila
 * plegada iba a ser la segunda. **Equivocarse acá no da error**: da una fecha
 * plausible y falsa. Un `03/11/2026` con los índices cruzados es el 11 de marzo,
 * y nada en la pantalla lo delata.
 *
 * ⚠️ **No valida que la fecha exista.** Un `31/02/2026` sale como `2026-02-31`,
 * y lo rechaza Zod en el contrato. Acá solo se cambia el orden: poner un
 * calendario en esta función es meterle criterio a un reordenamiento.
 */
export function fechaDesdeTecleada(tecleada: string): string | null {
  const [dd, mm, aaaa] = tecleada.trim().split('/');
  if (!dd || !mm || !aaaa) return null;
  if (dd.length !== 2 || mm.length !== 2 || aaaa.length !== 4) return null;
  return `${aaaa}-${mm}-${dd}`;
}

/**
 * El resumen de una respuesta **en una línea**, para la fila plegada.
 *
 * ⚠️ **Este es el punto donde el recorrido plegado se gana o se pierde.** Si un
 * tipo no se resume en una línea legible, plegarlo esconde la respuesta en vez de
 * mostrarla — y entonces la lista es peor que tenerlo todo desplegado. De acá
 * salió `abreEnPantallaCompleta`: los que solo se pueden resumir contando («4 de
 * 6 afirmaciones») no se pueden REVISAR desde la lista.
 *
 * Devuelve `null` cuando no hay nada que resumir. Quien dibuja pone en su lugar
 * «Sin responder»: el texto es de la interfaz, no de acá.
 */
export function resumenDeRespuesta(
  p: Pregunta,
  r: RespuestaResumible | undefined,
  escala: Escala | undefined,
): string | null {
  if (!r) return null;

  switch (p.tipo) {
    case 'likert': {
      if (r.punto === undefined) return null;
      const punto = escala?.puntos.find((x) => x.orden === r.punto);
      /*
        La etiqueta, no el número. «De acuerdo» dice algo; «4» obliga a recordar
        de qué escala era. Y si la etiqueta cabe en la pastilla —una escala 0–10—
        es un número de todos modos, así que se muestra tal cual (D-133).
      */
      return punto?.etiqueta.trim() || `Punto ${r.punto}`;
    }
    case 'barra': {
      if (r.punto === undefined) return null;
      const min = p.minimo ?? 0;
      const max = p.maximo ?? 100;
      // Los extremos por su nombre, el resto por su número: es lo que se recuerda.
      if (r.punto === min && p.etiquetaMin) return p.etiquetaMin;
      if (r.punto === max && p.etiquetaMax) return p.etiquetaMax;
      return `${r.punto} de ${max}`;
    }
    case 'opcion_unica': {
      const op = p.items.find((i) => i.id === r.opcion);
      return op?.texto ?? null;
    }
    case 'opcion_multiple': {
      const elegidas = p.items.filter((i) => r.opciones?.includes(i.id));
      if (elegidas.length === 0) return null;
      /*
        Hasta dos por su nombre; de ahí, «y N más». Escribir cinco opciones
        completas en una fila de teléfono las corta a la mitad, y una lista
        cortada a la mitad no se puede revisar.
      */
      if (elegidas.length <= 2) return elegidas.map((i) => i.texto).join(', ');
      return `${elegidas[0]!.texto}, ${elegidas[1]!.texto} y ${elegidas.length - 2} más`;
    }
    case 'si_no':
      return r.bool === undefined ? null : r.bool ? 'Sí' : 'No';
    case 'texto_corto':
    case 'texto_largo': {
      const t = (r.texto ?? '').trim();
      return t.length === 0 ? null : t;
    }
    case 'numero': {
      const n = (r.numero ?? '').trim();
      return n.length === 0 ? null : n;
    }
    case 'fecha': {
      if (!r.fecha) return null;
      const [a, m, d] = r.fecha.split('-');
      return d && m && a ? `${d}/${m}/${a}` : r.fecha;
    }
    case 'matriz_likert': {
      const hechas = p.items.filter((i) => r.matriz?.[i.id] !== undefined).length;
      if (hechas === 0) return null;
      /*
        ⚠️ Una matriz **no se puede resumir de verdad**: seis afirmaciones con
        seis respuestas distintas no caben en una línea. Se cuenta, y por eso
        este tipo se abre a pantalla completa para revisarlo.
      */
      return `${hechas} de ${p.items.length} afirmaciones`;
    }
    case 'archivo':
      return r.adjunto ? r.adjunto.nombre : null;
    case 'nota_voz':
    case 'video': {
      if (!r.adjunto) return null;
      const s = r.adjunto.segundos ?? 0;
      const reloj = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      return p.tipo === 'video' ? `Video de ${reloj}` : `Grabación de ${reloj}`;
    }
    case 'seccion':
      return null;
  }
}

/**
 * Los tipos que **se abren a pantalla completa** en vez de desplegarse en la
 * lista (CC-12).
 *
 * No es una lista de gustos: son los que **no caben en una fila de teléfono**.
 *
 * · `matriz_likert` — seis afirmaciones por cinco puntos es una tabla; apretada
 *   en una fila de lista, se toca el punto equivocado.
 * · `barra` — se arrastra con el dedo de un extremo al otro, y un recorrido de
 *   200 px de ancho convierte cada tramo en tres píxeles.
 * · `archivo`, `nota_voz` y `video` — el selector y el grabador necesitan sitio,
 *   y grabar la voz merece una pantalla que no compita con otras nueve preguntas.
 *
 * ⚠️ Los demás se despliegan EN la lista: abrir una pantalla entera para elegir
 * «Sí» o «No» son dos toques de más por pregunta, que en un cuestionario de
 * veinte son cuarenta.
 */
export const abreEnPantallaCompleta = (p: Pregunta) =>
  p.tipo === 'matriz_likert' ||
  p.tipo === 'barra' ||
  p.tipo === 'archivo' ||
  p.tipo === 'nota_voz' ||
  p.tipo === 'video';
